import { getPlatform, getPlatformOptimizedLink } from "./deepLinkHelpers";

const SAFE_PROTOCOLS = new Set(["http", "https", "mailto", "tel", "sms"]);

const TIMEOUTS = {
  IOS: 2000,
  ANDROID: 2500,
  DEFAULT: 2500,
} as const;

const QUICK_RETURN_THRESHOLD = 1000;

const isSafeProtocol = (url: string): boolean => {
  if (!url) return false;

  try {
    const urlObj = new URL(url);
    const protocol = urlObj.protocol.replace(":", "").toLowerCase();
    return SAFE_PROTOCOLS.has(protocol);
  } catch {
    const lowerUrl = url.toLowerCase();
    return Array.from(SAFE_PROTOCOLS).some((protocol) =>
      lowerUrl.startsWith(`${protocol}:`)
    );
  }
};

export const isMobilePlatform = (): boolean => getPlatform().isMobile;

export const isIOS = (): boolean => getPlatform().isIOS;

export const isAndroid = (): boolean => getPlatform().isAndroid;

/**
 * Handles smart link navigation with platform-aware fallback
 *
 * @param deepLink - The deep link URL (e.g., whatsapp://, telegram://, etc.)
 * @param fallbackLink - The HTTPS fallback URL
 * @param timeout - Optional timeout in milliseconds before falling back (auto-detected by platform if not provided)
 * @param openInNewTab - Whether to open the link in a new tab (default: true for desktop, false for mobile)
 *
 * @example
 * ```typescript
 * handleSmartLink(
 *   'whatsapp://send?phone=1234567890',
 *   'https://wa.me/1234567890',
 *   undefined,
 *   true
 * );
 * ```
 */
export const handleSmartLink = (
  deepLink: string,
  fallbackLink: string,
  timeout?: number,
  openInNewTab?: boolean
): void => {
  const platform = getPlatform();
  const shouldOpenInNewTab = platform.isMobile ? false : openInNewTab ?? true;

  if (!deepLink || !fallbackLink) {
    console.warn("handleSmartLink: Missing required parameters");
    if (fallbackLink && isSafeProtocol(fallbackLink)) {
      try {
        if (shouldOpenInNewTab) {
          window.open(fallbackLink, "_blank", "noopener,noreferrer");
        } else {
          window.location.href = fallbackLink;
        }
      } catch (error) {
        console.error("Error navigating to fallback link:", error);
      }
    }
    return;
  }

  if (!isSafeProtocol(fallbackLink)) {
    console.error(
      "handleSmartLink: Fallback URL must use a safe protocol (http, https, mailto, tel, sms)"
    );
    return;
  }

  let optimizedDeepLink: string;
  let optimizedFallback: string | undefined;

  try {
    const result = getPlatformOptimizedLink(deepLink, fallbackLink);
    optimizedDeepLink = result.primary;
    optimizedFallback = result.fallback;
  } catch (error) {
    console.error("Error optimizing links:", error);
    try {
      if (shouldOpenInNewTab) {
        window.open(fallbackLink, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = fallbackLink;
      }
    } catch (navError) {
      console.error("Error navigating to fallback:", navError);
    }
    return;
  }

  const fallbackTarget = (optimizedFallback ?? fallbackLink)?.trim();
  const usableFallback =
    fallbackTarget && fallbackTarget !== optimizedDeepLink
      ? fallbackTarget
      : undefined;
  const isMobile = platform.isMobile;

  if (!isMobile) {
    const targetUrl = usableFallback || optimizedDeepLink;
    try {
      if (shouldOpenInNewTab) {
        window.open(targetUrl, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = targetUrl;
      }
    } catch (error) {
      console.error("Error navigating on desktop:", error);
    }
    return;
  }

  const platformTimeout =
    timeout ??
    (platform.isIOS
      ? TIMEOUTS.IOS
      : platform.isAndroid
      ? TIMEOUTS.ANDROID
      : TIMEOUTS.DEFAULT);

  const startTime = Date.now();
  let hasNavigated = false;
  let cleanupDone = false;
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
  let finalCleanupTimer: ReturnType<typeof setTimeout> | undefined;

  const cleanup = () => {
    if (cleanupDone) return;
    cleanupDone = true;

    try {
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }
      if (finalCleanupTimer) {
        clearTimeout(finalCleanupTimer);
        finalCleanupTimer = undefined;
      }
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    } catch (error) {
      console.error("Error during cleanup:", error);
    }
  };

  if (usableFallback) {
    fallbackTimer = setTimeout(() => {
      if (!hasNavigated && !document.hidden) {
        try {
          if (shouldOpenInNewTab) {
            window.open(usableFallback, "_blank", "noopener,noreferrer");
          } else {
            window.location.href = usableFallback;
          }
        } catch (error) {
          console.error("Error navigating to fallback:", error);
        }
      }
    }, platformTimeout);
  }

  const onBlur = () => {
    hasNavigated = true;
    setTimeout(cleanup, 100);
  };

  const onFocus = () => {
    const elapsedTime = Date.now() - startTime;
    if (elapsedTime < QUICK_RETURN_THRESHOLD) {
      hasNavigated = false;
    }
  };

  const onVisibilityChange = () => {
    if (document.hidden) {
      hasNavigated = true;
      setTimeout(cleanup, 100);
    }
  };

  const onBeforeUnload = () => {
    hasNavigated = true;
    cleanup();
  };

  window.addEventListener("blur", onBlur);
  window.addEventListener("focus", onFocus);
  window.addEventListener("beforeunload", onBeforeUnload);
  document.addEventListener("visibilitychange", onVisibilityChange);

  try {
    if (platform.isIOS) {
      const opened = window.open(optimizedDeepLink, "_self");
      if (!opened) {
        window.location.href = optimizedDeepLink;
      }
    } else {
      window.location.href = optimizedDeepLink;
    }
  } catch (error) {
    console.error("Error opening deep link:", error);
    cleanup();
    const recoveryTarget = usableFallback || optimizedDeepLink;
    try {
      if (shouldOpenInNewTab && usableFallback) {
        window.open(recoveryTarget, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = recoveryTarget;
      }
    } catch (recoveryError) {
      console.error("Error navigating to recovery target:", recoveryError);
    }
  }

  finalCleanupTimer = setTimeout(cleanup, platformTimeout + 1000);
};
